/**
 * Centralized Logging Service
 *
 * Provides environment-aware logging with different log levels.
 * In production, only errors and warnings are logged.
 * In development, all log levels are active.
 *
 * @module logger
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerConfig {
  enabledLevels: Set<LogLevel>;
  enableRemoteLogging: boolean;
  remoteEndpoint?: string;
}

class Logger {
  private config: LoggerConfig;
  private isProduction: boolean;

  constructor() {
    this.isProduction = process.env.NODE_ENV === 'production';

    // Configure which log levels are enabled based on environment
    this.config = {
      enabledLevels: this.isProduction
        ? new Set<LogLevel>(['warn', 'error'])
        : new Set<LogLevel>(['debug', 'info', 'warn', 'error']),
      enableRemoteLogging: this.isProduction,
      remoteEndpoint: process.env.REACT_APP_LOG_ENDPOINT
    };
  }

  /**
   * Format log message with timestamp and context
   */
  private formatMessage(level: LogLevel, message: string, context?: Record<string, any>): string {
    const timestamp = new Date().toISOString();
    const contextStr = context ? ` ${JSON.stringify(context)}` : '';
    return `[${level.toUpperCase()} ${timestamp}] ${message}${contextStr}`;
  }

  /**
   * Send logs to remote logging service (e.g., Sentry, LogRocket)
   */
  private async sendToRemote(level: LogLevel, message: string, data?: any): Promise<void> {
    if (!this.config.enableRemoteLogging || !this.config.remoteEndpoint) {
      return;
    }

    try {
      // In production, you would send to your logging service
      // For now, this is a placeholder
      // Example: await fetch(this.config.remoteEndpoint, { method: 'POST', body: JSON.stringify({ level, message, data }) });
    } catch (error) {
      // Silently fail remote logging to prevent cascading errors
      console.error('Failed to send log to remote service:', error);
    }
  }

  /**
   * Debug level logging - development only
   * Use for detailed debugging information
   */
  debug(message: string, ...args: any[]): void {
    if (!this.config.enabledLevels.has('debug')) return;

    const formattedMessage = this.formatMessage('debug', message);
    console.log(formattedMessage, ...args);
  }

  /**
   * Info level logging
   * Use for general informational messages
   */
  info(message: string, ...args: any[]): void {
    if (!this.config.enabledLevels.has('info')) return;

    const formattedMessage = this.formatMessage('info', message);
    console.log(formattedMessage, ...args);
  }

  /**
   * Warning level logging
   * Use for potentially problematic situations
   */
  warn(message: string, ...args: any[]): void {
    if (!this.config.enabledLevels.has('warn')) return;

    const formattedMessage = this.formatMessage('warn', message);
    console.warn(formattedMessage, ...args);

    // Send warnings to remote logging in production
    if (this.isProduction) {
      this.sendToRemote('warn', message, args);
    }
  }

  /**
   * Error level logging
   * Use for error conditions that need attention
   */
  error(message: string, error?: Error | any, ...args: any[]): void {
    if (!this.config.enabledLevels.has('error')) return;

    const formattedMessage = this.formatMessage('error', message);
    const errorDetails = error instanceof Error ? {
      message: error.message,
      stack: error.stack,
      name: error.name
    } : error;

    console.error(formattedMessage, errorDetails, ...args);

    // Always send errors to remote logging
    this.sendToRemote('error', message, { error: errorDetails, args });
  }

  /**
   * Log Firebase-specific operations
   */
  firebase(operation: string, details?: any): void {
    this.debug(`[Firebase] ${operation}`, details);
  }

  /**
   * Log performance metrics
   */
  performance(metric: string, value: number, unit: string = 'ms'): void {
    this.debug(`[Performance] ${metric}: ${value}${unit}`);
  }

  /**
   * Log user interactions (for analytics)
   */
  userAction(action: string, details?: Record<string, any>): void {
    this.info(`[UserAction] ${action}`, details);
  }
}

// Export singleton instance
export const logger = new Logger();

// Export for backwards compatibility (can be removed after migration)
export default logger;
